/* Badge labels.
 *
 * Presentation metadata, not data: the set is closed, it is defined by
 * `Badge['id']` in types.ts, and a partner's badges arrive from the API as
 * those ids. So the words live in the client and travel with the build, the
 * same way the tab bar's labels do.
 *
 * They sit here rather than in `seed/catalog.ts`, where they used to, because
 * anything importing from `seed/` drags the entire seeded market into the
 * bundle — 57 kB of providers, price lists and reviews that an app talking to
 * a real backend has no use for. Two components needed one label lookup and
 * that was enough to pin the whole catalog into every build.
 *
 * Not in the i18n bundles for a different reason: these are keyed by an id the
 * API sends, so the lookup has to be total over a runtime value. A missing
 * translation key renders as a dotted path; a missing entry here renders as
 * the id, which is at least a word.
 */

import type { Badge, LocalizedText } from './types'

const BADGE_LABELS: Record<Badge['id'], LocalizedText> = {
  /* The platform's two, set from evidence rather than by an operator. Both
     have been arriving from the API since B10 and neither had an entry here,
     so an Arabic reader got the Latin word — the failure `badgeLabel`'s
     fallback is designed to make survivable and not to make invisible. */
  verified: { en: 'Verified', ar: 'موثّق' },
  insured: { en: 'Insured', ar: 'مؤمّن' },
  'top-rated': { en: 'Top rated', ar: 'الأعلى تقييماً' },
  fast: { en: 'Fast', ar: 'سريع' },
  eco: { en: 'Eco', ar: 'صديق للبيئة' },
  new: { en: 'New', ar: 'جديد' },
  express: { en: 'Express', ar: 'خدمة سريعة' },
}

export function badgeLabel(id: string): LocalizedText {
  return BADGE_LABELS[id as Badge['id']] ?? { en: id, ar: id }
}

/** Narrows what the API sent to what this app can name.
 *
 *  The wire types `badges` as `string[]`, because Go has no closed set for it
 *  and an eighth badge is one constant away. Dropping the ones we have no word
 *  for is the honest boundary behaviour: a badge is a claim about a partner,
 *  and a claim rendered as a bare id — `insured`, in Latin, in an Arabic
 *  interface — reads as a bug to the person it is supposed to reassure.
 *
 *  Silent on purpose. An unknown badge is the server being ahead of this
 *  build, which is the normal state of affairs between two things that deploy
 *  separately, not an error worth a report. */
export function knownBadges(ids: readonly string[] | null | undefined): Badge['id'][] {
  return (ids ?? []).filter((id): id is Badge['id'] => id in BADGE_LABELS)
}
